import { AppProps } from 'next/app';
import { appWithTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { AuthGuard } from '@restworld/feature-authentication';
import { BotChallengeHost } from '@restworld/ui-components';
import { Spinner } from '@restworld/ui-ds-base';

import nextI18NextConfig from '../next-i18next.config';
import GlobalStyles from '../styles/GlobalStyles';
import '../styles/globals.css';
import '@restworld/utils-common/iubenda-banner.css';
import ErrorBoundaryWrapper from '../components/error-boundary';
import { MainContextWrapper } from '../context';
import { ProgressBar } from '../utility-components/progress-bar';
import { useRecoverMissingPageTranslations } from '../libs/use-recover-missing-page-translations';
import { useTryForFreeAnalyticsTags } from '../components/organization-onboarding/try-for-free-variant';
import { CanonicalUrl, initOpenObserveRum, translationGuardConfig } from '@restworld/utils-common';

const TestEnvironmentFrame: React.FunctionComponent = () => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <>
      {/* Yellow border frame around the entire viewport */}
      <div className="fixed inset-0 pointer-events-none z-[9999]" style={{ border: '2px solid #eab308' }} />
      {/* TEST badge - top right */}
      <div className="fixed top-2 right-2 z-[9999]">
        <div
          className="relative cursor-pointer"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <span className="inline-block px-1.5 py-0.5 bg-yellow-400 text-yellow-900 text-[10px] font-bold rounded leading-none tracking-wide">
            TEST
          </span>
          {showTooltip && (
            <div className="absolute right-0 top-full mt-1 px-2 py-1 bg-yellow-400 text-yellow-900 text-xs font-semibold rounded shadow whitespace-nowrap">
              Ambiente di test
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// trigger redeploy
function CustomApp({ Component, pageProps }: AppProps) {
  const router = useRouter();
  useEffect(() => {
    // No-op unless NEXT_PUBLIC_OPENOBSERVE_RUM_CLIENT_TOKEN is set.
    initOpenObserveRum({ service: 'restworld-employer-app' });
  }, []);
  useRecoverMissingPageTranslations();
  useTryForFreeAnalyticsTags();
  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      // eslint-disable-next-line no-console
      console.trace('Route change trace:', url);
    };
    router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router.events]);
  return (
    <>
      <CanonicalUrl />
      <GlobalStyles />
      <ErrorBoundaryWrapper>
        <MainContextWrapper>
          {process.env.NEXT_PUBLIC_APP_ENV !== 'production' && <TestEnvironmentFrame />}
          <ProgressBar />
          <AuthGuard loader={<Spinner size="lg" color="primary" />}>
            <Component {...pageProps} />
          </AuthGuard>
          {/* Cloudflare Turnstile checkbox, shown only when Cloudflare wants a click (bot-challenge.ts). */}
          <BotChallengeHost />
        </MainContextWrapper>
      </ErrorBoundaryWrapper>
    </>
  );
}

// The guard loads, on the client, any namespace a screen needs that its page did not ship, and
// reports each case to O2 (see translation-guard.ts).
export default appWithTranslation(CustomApp, { ...nextI18NextConfig, ...translationGuardConfig });
